import { useEffect, useMemo } from 'react';
import urlTemplate from 'url-template';
import type { MyTransactionsRequest } from 'src/apis/capacity-management/use-my-transactions';
import type { ReshuffleActionsResponse } from 'src/apis/reshuffle-service/types';
import type { OptionalReshuffleServiceRootRequest } from 'src/apis/reshuffle-service/use-reshuffle-service';
import { getAllocationIds } from 'src/apis/reshuffle-service/utils';
import { useAxios } from 'src/hooks/use-axios';

type ExecuteProps = { url: string };

export function useReshuffleAllocationActions(
  myTransactionsRequest: MyTransactionsRequest,
  reshuffleServiceRoot: OptionalReshuffleServiceRootRequest
) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<ReshuffleActionsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  const url = useMemo(() => {
    const getAllocationsUrl =
      reshuffleServiceRoot?.response?.data._links?.getAllocations?.href;
    const items = myTransactionsRequest.response?.data._embedded?.items;

    if (!getAllocationsUrl || !items) {
      return;
    }

    const ids = items.flatMap(getAllocationIds);

    if (ids.length === 0) {
      return;
    }

    return urlTemplate.parse(getAllocationsUrl).expand({ ids });
  }, [myTransactionsRequest.response, reshuffleServiceRoot?.response]);

  useEffect(() => {
    if (!url) {
      return;
    }

    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [url]);

  return request;
}

export type ReshuffleAllocationActionsRequest = ReturnType<
  typeof useReshuffleAllocationActions
>;
